import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Mail, Smartphone, BellRing, RotateCcw, Ban, KeyRound, Hourglass, CheckCircle2, XCircle, Clock, Link2, UserPlus, User, Fingerprint } from 'lucide-react';
import { listMocLinkRequests, resendMocLinkOtp, cancelMocLinkRequest } from '../../redux/mocLinkSlice';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime } from '../../utils/clock';
import MocLinkModal from './MocLinkModal';

// PENDING LINKS — the desk's view of Mitra One Care link requests it raised
// (GET /moc-link/requests). A pending Approval-channel request is the reason
// a booking can't happen yet; a pending OTP one can still be finished here
// ("Enter code"). History (confirmed / declined / expired) is the consent
// record. `mine` limits the list to the caller's own requests (doctors).

const STATUSES = ['pending', 'confirmed', 'declined', 'expired'];

const fmt = (iso, lang) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    // The time part is the one clock format ("2:30 PM", utils/clock) in every language.
    const day = d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${day}, ${formatTime(d)}`;
};

const fullName = (o) => [o?.name, o?.surname].filter(Boolean).join(' ').trim();

export default function MocLinkRequestsPanel({ mine = false }) {
    const { t, i18n } = useTranslation('mocLink');
    const lang = i18n.language || 'en';
    const dispatch = useDispatch();
    const { requests, requestsLoading, requestsError, pendingCount, decidedTick } = useSelector((s) => s.mocLink);
    const [filter, setFilter] = useState('pending');
    const [busyId, setBusyId] = useState(null);
    const [codeFor, setCodeFor] = useState(null); // request re-opened at its code step
    const [cooldown, setCooldown] = useState({}); // linkRequestId → seconds until Resend is allowed (429 RESEND_TOO_SOON)

    useEffect(() => {
        if (!Object.values(cooldown).some((n) => n > 0)) return undefined;
        const id = setInterval(() => setCooldown((c) => {
            const next = {};
            Object.entries(c).forEach(([k, v]) => { if (v > 1) next[k] = v - 1; });
            return next;
        }), 1000);
        return () => clearInterval(id);
    }, [cooldown]);

    const load = useCallback(() => {
        dispatch(listMocLinkRequests({ status: filter, mine }));
    }, [dispatch, filter, mine]);

    // decidedTick: a patient answered in their app (socket) → re-list.
    useEffect(() => { load(); }, [load, decidedTick]);

    const channelChip = (ch) => {
        const map = {
            email: { icon: <Mail size={12} />, label: t('mocLink.channel_email', 'Email OTP'), cls: 'bg-[#4ABDBD]/10 text-[#2D8A8A]' },
            sms: { icon: <Smartphone size={12} />, label: t('mocLink.channel_sms', 'Mobile OTP'), cls: 'bg-[#4ABDBD]/10 text-[#2D8A8A]' },
            approval: { icon: <BellRing size={12} />, label: t('mocLink.channel_approval', 'Approval in patient app'), cls: 'bg-[#D7A05C]/15 text-[#a8753a]' },
            // Desk-witnessed: no code was sent, a staff member attested to it.
            desk: { icon: <Fingerprint size={12} />, label: t('mocLink.chip_desk', 'Desk'), cls: 'bg-[#9DB3B1]/25 text-[#3f5a58]' },
        };
        const c = map[ch] || map.email;
        return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold ${c.cls}`}>{c.icon}{c.label}</span>;
    };

    const statusChip = (st) => {
        const map = {
            pending: { icon: <Hourglass size={12} />, label: t('mocLink.status_pending', 'Waiting'), cls: 'bg-amber-50 text-amber-700' },
            confirmed: { icon: <CheckCircle2 size={12} />, label: t('mocLink.status_confirmed', 'Linked'), cls: 'bg-emerald-50 text-emerald-700' },
            declined: { icon: <XCircle size={12} />, label: t('mocLink.status_declined', 'Declined'), cls: 'bg-red-50 text-red-600' },
            expired: { icon: <Clock size={12} />, label: t('mocLink.status_expired', 'Expired'), cls: 'bg-gray-100 text-gray-500' },
        };
        const c = map[st] || map.pending;
        return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold ${c.cls}`}>{c.icon}{c.label}</span>;
    };

    const resend = async (r) => {
        setBusyId(r.linkRequestId);
        try {
            const res = await dispatch(resendMocLinkOtp({ linkRequestId: r.linkRequestId }));
            if (resendMocLinkOtp.fulfilled.match(res)) {
                toast.info(t('mocLink.resent', 'Code re-sent'));
                setCooldown((c) => ({ ...c, [r.linkRequestId]: 60 }));
                load();
            } else if (res.payload?.code === 'RESEND_TOO_SOON') {
                const secs = Math.max(1, Math.ceil((Number(res.payload.retryAfterMs) || 60000) / 1000));
                setCooldown((c) => ({ ...c, [r.linkRequestId]: secs }));
                toast.info(t('mocLink.resend_too_soon', { seconds: secs, defaultValue: `Please wait ${secs}s before resending` }));
            } else if (['RESEND_LIMIT', 'EXPIRED', 'ALREADY_DECIDED'].includes(res.payload?.code)) {
                // The request is gone (the 4th resend expires it) — it leaves the pending list.
                toast.warn(t('mocLink.resend_limit', 'Too many resends — this request has expired. Start again.'));
                load();
            } else if (res.payload?.code === 'SMS_UNAVAILABLE') {
                toast.warn(t('mocLink.sms_unavailable', 'SMS delivery is not configured here — use Email OTP or Approval instead.'));
            } else {
                toast.warn(extractErrorMessage(res.payload, t('mocLink.resend_failed', 'Could not resend'), lang));
            }
        } finally {
            setBusyId(null);
        }
    };

    const cancel = async (r) => {
        const who = fullName(r.patient) || fullName(r.draftPatient) || t('mocLink.the_patient', 'The patient');
        const ok = await Swal.fire({
            title: t('mocLink.cancel_confirm_title', 'Cancel link request?'),
            text: t('mocLink.cancel_confirm_text', { name: who, defaultValue: `The request for ${who} will be withdrawn. You can raise a new one later.` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('mocLink.cancel_confirm_button', 'Yes, cancel it'),
            cancelButtonText: t('mocLink.keep', 'Keep'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
        });
        if (!ok.isConfirmed) return;
        setBusyId(r.linkRequestId);
        try {
            const res = await dispatch(cancelMocLinkRequest({ linkRequestId: r.linkRequestId }));
            if (cancelMocLinkRequest.fulfilled.match(res)) {
                toast.info(t('mocLink.cancelled', 'Link request cancelled'));
                load();
            } else {
                toast.warn(extractErrorMessage(res.payload, t('mocLink.cancel_failed', 'Could not cancel the request'), lang));
            }
        } finally {
            setBusyId(null);
        }
    };

    const tabLabel = (st) => ({
        pending: t('mocLink.tab_pending', 'Pending'),
        confirmed: t('mocLink.tab_confirmed', 'Linked'),
        declined: t('mocLink.tab_declined', 'Declined'),
        expired: t('mocLink.tab_expired', 'Expired'),
    })[st];

    return (
        <div data-testid="moc-link-requests-panel">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="inline-flex bg-gray-100 rounded-xl p-1" data-testid="moc-link-requests-tabs">
                    {STATUSES.map((st) => (
                        <button
                            key={st}
                            type="button"
                            onClick={() => setFilter(st)}
                            data-testid={`moc-link-tab-${st}`}
                            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 ${filter === st ? 'bg-white text-[#2D8A8A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {tabLabel(st)}
                            {st === 'pending' && pendingCount > 0 && (
                                <span className="rounded-full bg-[#4ABDBD] px-1.5 py-px text-[10px] font-bold text-white">{pendingCount}</span>
                            )}
                        </button>
                    ))}
                </div>
                <p className="text-xs text-gray-400">
                    {filter === 'pending'
                        ? t('mocLink.panel_hint_pending', 'Approval requests wait for the patient in their app; OTP requests can be finished here with the code.')
                        : t('mocLink.panel_hint_history', 'The consent record — who asked, how, and what the patient answered.')}
                </p>
            </div>

            {requestsLoading && requests.length === 0 ? (
                <div className="flex justify-center py-12">
                    <div className="h-8 w-8 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
                </div>
            ) : requestsError ? (
                <p className="text-sm text-red-500 py-8 text-center">{extractErrorMessage(requestsError, t('mocLink.load_failed', 'Could not check for an account'), lang)}</p>
            ) : requests.length === 0 ? (
                <div className="py-12 text-center">
                    <Link2 size={36} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">
                        {filter === 'pending' ? t('mocLink.none_pending', 'No link requests waiting.') : t('mocLink.none_history', 'Nothing here yet.')}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {requests.map((r) => {
                        const name = fullName(r.patient) || fullName(r.draftPatient) || t('mocLink.unnamed', 'Unnamed patient');
                        const isDraft = !r.patient && r.draftPatient;
                        const draftContact = [r.draftPatient?.email, r.draftPatient?.phone].filter(Boolean).join(' · ');
                        const busy = busyId === r.linkRequestId;
                        const wait = cooldown[r.linkRequestId] || 0;
                        const otpChannel = r.channel === 'email' || r.channel === 'sms';
                        return (
                            <div key={r.linkRequestId} data-testid={`moc-link-request-${r.linkRequestId}`}
                                className={`rounded-2xl border bg-white shadow-sm p-4 flex flex-col md:flex-row md:items-center gap-3 ${r.status === 'pending' ? 'border-amber-200' : 'border-gray-200'}`}>
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                    <span className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${isDraft ? 'bg-[#D7A05C]/15 text-[#a8753a]' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`} title={isDraft ? t('mocLink.kind_draft', 'New Mitra One Care profile (typed at the desk)') : t('mocLink.kind_existing', 'Existing Mitra One Care profile')}>
                                        {isDraft ? <UserPlus size={16} /> : <User size={16} />}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-gray-800 text-sm">{name}</span>
                                            {channelChip(r.channel)}
                                            {statusChip(r.status)}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1 break-all">
                                            {r.contact && <span>{t('mocLink.sent_to', 'Sent to')} <span className="font-medium text-gray-700">{r.contact}</span></span>}
                                            {r.contact && draftContact && <span> · </span>}
                                            {draftContact && <span>{t('mocLink.typed', 'Typed')}: {draftContact}</span>}
                                        </p>
                                        {r.channel === 'desk' && r.attestReason && (
                                            <p className="text-xs text-gray-600 mt-1 italic break-words" data-testid={`moc-link-attest-${r.linkRequestId}`}>
                                                “{r.attestReason}”
                                            </p>
                                        )}
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            {r.requestedByName && <span>{t('mocLink.by', 'By')} {r.requestedByName} · </span>}
                                            <span>{t('mocLink.raised', 'Raised')} {fmt(r.created_at, lang)}</span>
                                            {r.status === 'pending' && r.expiresAt && <span> · {t('mocLink.expires', 'Expires')} {fmt(r.expiresAt, lang)}</span>}
                                            {r.status !== 'pending' && r.decidedAt && <span> · {t('mocLink.decided', 'Decided')} {fmt(r.decidedAt, lang)}</span>}
                                        </p>
                                    </div>
                                </div>
                                {r.status === 'pending' && (
                                    <div className="flex items-center gap-2 shrink-0 md:justify-end flex-wrap">
                                        {otpChannel && (
                                            <button type="button" onClick={() => setCodeFor(r)} disabled={busy}
                                                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer"
                                                data-testid={`moc-link-enter-code-${r.linkRequestId}`}>
                                                <KeyRound size={12} /> {t('mocLink.enter_code', 'Enter code')}
                                            </button>
                                        )}
                                        {otpChannel && (
                                            <button type="button" onClick={() => resend(r)} disabled={busy || wait > 0}
                                                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#4ABDBD] text-[#2D8A8A] font-semibold hover:bg-[#ecfdfd] transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                                data-testid={`moc-link-resend-${r.linkRequestId}`}>
                                                <RotateCcw size={12} />
                                                {wait > 0
                                                    ? t('mocLink.resend_in', { time: `${Math.floor(wait / 60)}:${String(wait % 60).padStart(2, '0')}`, defaultValue: `Resend in ${wait}s` })
                                                    : t('mocLink.resend', 'Resend code')}
                                            </button>
                                        )}
                                        <button type="button" onClick={() => cancel(r)} disabled={busy}
                                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 font-semibold hover:border-red-300 hover:text-red-500 transition-all disabled:opacity-50 cursor-pointer"
                                            data-testid={`moc-link-cancel-${r.linkRequestId}`}>
                                            <Ban size={12} /> {t('mocLink.cancel_request', 'Cancel request')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <MocLinkModal
                open={Boolean(codeFor)}
                existingRequest={codeFor}
                patientName={codeFor ? (fullName(codeFor.patient) || fullName(codeFor.draftPatient)) : ''}
                onLinked={() => load()}
                onClose={({ changed } = {}) => { setCodeFor(null); if (changed) load(); }}
            />
        </div>
    );
}
